import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { KeyRound, ShieldCheck, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Carte « Cle secrete Stripe » : champ write-only.
 * - Affiche uniquement un indicateur masque (sk_live_****1234) + date.
 * - L'enregistrement valide la cle aupres de Stripe sans jamais la re-afficher.
 */
const AdminStripeKey = () => {
  const [status, setStatus] = useState(null); // { configured, masked, mode, source, configured_at }
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);

  const loadStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/stripe-key`);
      setStatus(res.data);
    } catch (e) {
      console.error('Erreur chargement statut cle Stripe', e);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const formatDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString('fr-FR'); } catch { return iso; }
  };

  const handleSave = async () => {
    const key = apiKey.trim();
    if (!key) {
      toast.error('Collez une cle Stripe (sk_live_…, sk_test_…)');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/stripe-key`, { api_key: key });
      if (res.data?.valid) {
        toast.success(`Cle valide ✓ enregistree (${res.data.mode})`);
        setApiKey('');
        await loadStatus();
      } else {
        toast.error(`Cle invalide ✗ — ${res.data?.detail || 'refusee par Stripe'} (cle precedente conservee)`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 p-4 bg-gray-800/30 rounded-lg border border-purple-700/50" data-testid="admin-stripe-key-card">
      <div className="flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-purple-400" />
        <h4 className="text-white font-semibold">Cle secrete Stripe</h4>
      </div>

      {/* Indicateur masque */}
      <div className="text-sm" data-testid="stripe-key-status">
        {status == null ? (
          <span className="text-gray-500">Chargement…</span>
        ) : status.configured ? (
          <span className="text-green-400 flex items-center gap-2 flex-wrap">
            <ShieldCheck className="w-4 h-4" />
            <span className="font-mono">{status.masked}</span>
            <span className="text-gray-400">
              ({status.mode}{status.source === 'env' ? ' — fichier serveur' : ''})
              {status.configured_at ? ` — configuree le ${formatDate(status.configured_at)}` : ''}
            </span>
          </span>
        ) : (
          <span className="text-yellow-400">Non configuree</span>
        )}
      </div>

      {/* Saisie write-only */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="sk_live_…  ou  sk_test_…"
          autoComplete="off"
          className="input-dark font-mono"
          data-testid="stripe-key-input"
        />
        <Button
          onClick={handleSave}
          disabled={saving}
          className="btn-neon whitespace-nowrap"
          data-testid="stripe-key-save"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Validation…' : 'Enregistrer'}
        </Button>
      </div>

      <p className="text-xs text-gray-500">
        La cle est validee aupres de Stripe, chiffree et n'est jamais re-affichee.
        Une cle definie ici prime sur le fichier serveur.
      </p>
    </div>
  );
};

export default AdminStripeKey;
